import moment from "moment";
import {
  capitalizeWordsSeperatedByUnderScore,
  isValidString,
} from "../../../../utils/stringModifications";
import AssistSessionScreenshotButton from "./assistSessionScreenshotButton";
import { buildActionLabel } from "./assistSessionViewer.helpers";
import {
  AssistActionMetadata,
  AssistQuestionMetadata,
  AssistSessionEntry,
} from "./assistSessionViewer.types";

interface AssistSessionQuestionCardProps {
  timestamp?: string;
  metadata: AssistQuestionMetadata;
  actions: AssistSessionEntry[];
}

/**
 * Renders one question/answer exchange of a RISA-Assist session.
 *
 * The full answer is shown without truncation, followed by a footer carrying
 * the source the assistant answered from and any screenshots it captured
 * while answering.
 *
 * @param props - Timestamp, question metadata and the actions it triggered
 * @returns The question entry element
 */
const AssistSessionQuestionCard = (props: AssistSessionQuestionCardProps) => {
  const { question, answer, answer_source, error_info } = props.metadata;

  return (
    <div className="assist-question-card--container flex flex-col gap-1.5 py-3.5">
      <div className="assist-question-card--header flex items-baseline gap-3">
        <div className="question-text flex-1 text-small font-semibold text-primaryGray-1">
          {question}
        </div>
        <div
          className="timestamp whitespace-nowrap text-x-tiny text-primaryGray-10"
          title={moment(props.timestamp).format("MM/DD/YYYY HH:mm:ss")}
        >
          {moment(props.timestamp).format("HH:mm")}
        </div>
      </div>
      {isValidString(error_info) ? (
        <div className="assist-question-card--error rounded-md bg-[#FEF3F2] px-2 py-1.5 text-small text-[#D92D20]">
          {error_info}
        </div>
      ) : (
        <div className="assist-question-card--answer whitespace-pre-wrap break-words text-small leading-relaxed text-primaryGray-4">
          {isValidString(answer) ? answer : "No answer recorded."}
        </div>
      )}
      <div className="assist-question-card--footer flex flex-wrap items-center gap-1.5 pt-0.5">
        {isValidString(answer_source) && (
          <span className="answer-source rounded-full bg-primaryGray-16 px-2 py-0.5 text-x-tiny font-medium text-primaryGray-7">
            {capitalizeWordsSeperatedByUnderScore(answer_source)}
          </span>
        )}
        {props.actions.map((action, index) => {
          const metadata = (action.metadata ?? {}) as AssistActionMetadata;
          const label = buildActionLabel(
            capitalizeWordsSeperatedByUnderScore(metadata.action_type ?? ""),
            metadata.tab,
            metadata.action_value,
          );
          return isValidString(metadata.screenshot_path) ? (
            <AssistSessionScreenshotButton
              key={`${action.timestamp}-${index}`}
              label={label}
              screenshotPath={metadata.screenshot_path ?? ""}
            />
          ) : (
            <span
              className="assist-action-chip max-w-full truncate rounded-full bg-primaryGray-16 px-2 py-0.5 text-x-tiny text-primaryGray-7"
              key={`${action.timestamp}-${index}`}
            >
              {label}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default AssistSessionQuestionCard;
